/* eslint-disable string-to-lingui/missing-lingui-transformation */
import {useNavigate} from 'react-router-dom'
import {styled} from 'styled-components'

import {Button, Inline, Stack, Text, tokens} from '@pleo-io/telescope'
import {ArrowRight, UserPlus} from '@pleo-io/telescope-icons'

import * as tracking from '@product-web/analytics'
import {reportError} from '@product-web/error/report'

import {MilestoneAccordion} from './milestone-accordion'

import {bff} from '../bff-hooks'
import type {MilestoneName, MilestoneStatus, Task} from '../universal/types'

type InviteTeamMilestoneAccordionItemProps = {
    status: MilestoneStatus
    tasks: Task[]
    milestoneName: MilestoneName
}

export const InviteTeamMilestoneAccordionItem = ({
    status,
    tasks,
    milestoneName,
}: InviteTeamMilestoneAccordionItemProps) => {
    const navigate = useNavigate()
    const {mutate: startTask} =
        bff.companyOnboarding.getStarted.startCompanyOnboardingTask.useMutation()

    return (
        <MilestoneAccordion.Item value={milestoneName} status={status}>
            <MilestoneAccordion.Header
                headingLevel="h2"
                heading="Invite your team"
                Icon={UserPlus}
                progress={getMilestoneProgress(tasks)}
            />

            <MilestoneAccordion.Content>
                {tasks.map((task) => {
                    switch (task.name) {
                        case 'INVITE_TEAM':
                            if (task.status === 'INCOMPLETE') {
                                return (
                                    <MilestoneAccordion.ContentItem key={task.name}>
                                        <ContentWrapper>
                                            <TextWrapper>
                                                <Text
                                                    as="h3"
                                                    variant="large-accent"
                                                    weight="medium"
                                                >
                                                    Ready for the next stage?
                                                </Text>

                                                <Text>
                                                    Invite up to <strong>2</strong> people for free.
                                                </Text>
                                            </TextWrapper>

                                            <Button
                                                variant="primary"
                                                IconRight={ArrowRight}
                                                onClick={() => {
                                                    startTask(task.name)
                                                    tracking.companyOnboardingMilestoneActioned({
                                                        action: 'started',
                                                        milestone: milestoneName,
                                                        task: task.name,
                                                    })
                                                    navigate('/onboarding/onboard-team')
                                                }}
                                            >
                                                Add people
                                            </Button>
                                        </ContentWrapper>
                                    </MilestoneAccordion.ContentItem>
                                )
                            }

                            return (
                                <MilestoneAccordion.ContentItem key={task.name}>
                                    <Text variant="large-accent">
                                        You've successfully invited people to Pleo
                                    </Text>
                                </MilestoneAccordion.ContentItem>
                            )
                        default: {
                            reportError('Invalid milestone task name', null, {
                                milestoneName,
                                taskName: task.name,
                            })

                            return null
                        }
                    }
                })}
            </MilestoneAccordion.Content>
        </MilestoneAccordion.Item>
    )
}

const getMilestoneProgress = <T extends Pick<Task, 'status'>>(tasks: T[]) => ({
    total: tasks.length,
    completed: tasks.filter((task) => task.status === 'COMPLETE').length,
})

const ContentWrapper = styled(Inline).attrs({
    wrap: true,
    space: 8,
    alignX: 'space-between',
    alignY: 'center',
})`
    row-gap: ${tokens.spacing8};
`

const TextWrapper = styled(Stack).attrs({space: 8})`
    width: auto;
`
